// src/components/ManageEnvironments.tsx

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Autocomplete,
  Menu,
  MenuItem,
  Popover,
  Select,
  Skeleton,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FolderIcon from "@mui/icons-material/Folder";
import GroupIcon from "@mui/icons-material/Group";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import HubIcon from "@mui/icons-material/Hub";
import VideoGlassContainer from "./dashboards/VideoGlassContainer";
import StorageIcon from "@mui/icons-material/Storage";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import BackupIcon from "@mui/icons-material/Backup";
import RestoreIcon from "@mui/icons-material/Restore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useSnackbar } from "notistack";
import { useFeatureGate } from "../hooks/useFeatureGate";
import api from "../api/api";
import logger from "utils/logger";
import { athenaColors } from "../theme/palette";
import { glass } from "../theme/glassTokens";
import {
  athenaDarkPaperSx,
  athenaBackdropSx,
  athenaDarkTitleSx,
  athenaDarkContentSx,
} from "./layout/AthenaDialogStyles";
import InviteToConsortiumDialog from "./InviteToConsortiumDialog";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Env {
  id: string;
  name: string;
  owner_organization?: { id: string; name: string } | null;
  allow_export: boolean;
  allow_ai_extraction: boolean;
  require_patient_anonymization: boolean;
  administrators?: { id: string; email: string; username?: string }[];
  patient_count?: number;
  members_count?: number;
}

interface EnvMember {
  id: number;
  user: {
    id: string;
    email: string;
    username?: string;
    first_name?: string;
    last_name?: string;
  };
  role: "owner" | "admin" | "editor" | "contributor" | "viewer";
  can_export: boolean;
  can_use_ai: boolean;
  can_view_patient_names: boolean;
  is_active: boolean;
  invited_at: string;
  accepted_at?: string;
  status?: "pending" | "active" | "declined";
}

// /environments/{id}/consortium/overview/ — the topology plus the export-reach report.
interface ConsortiumCentre {
  id: string;
  name: string;
  allow_export: boolean;
  patient_count: number;
  members_count: number;
}
interface ConsortiumReachRow {
  user_id: number;
  email: string;
  exportable_env_ids: string[];
  reaches_whole_network: boolean;
}
interface ConsortiumOverview {
  consortium: { id: string; name: string; allow_export: boolean };
  centres: ConsortiumCentre[];
  network_reach: ConsortiumReachRow[];
}

// A user's membership in one env, as returned by /environments/shared-users/.
interface SharedUserEnv {
  env_id: string;
  env_name: string;
  kind?: string;
  role: string;
  membership_id: number;
  can_manage: boolean;
}

interface SharedUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  environments: SharedUserEnv[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROLE_OPTIONS = [
  { value: "owner", label: "Owner", description: "Full control, can delete environment" },
  { value: "admin", label: "Admin", description: "Full control except delete" },
  { value: "editor", label: "Editor", description: "Can view, add, and edit" },
  { value: "contributor", label: "Contributor", description: "Can view and add only" },
  { value: "viewer", label: "Viewer", description: "Read-only access" },
];

const getRoleColor = (role: string) => {
  switch (role) {
    case "owner": return "error";
    case "admin": return "warning";
    case "editor": return "primary";
    case "contributor": return "info";
    case "viewer": return "default";
    default: return "default";
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ManageEnvironments: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { tier, quotas, canUse, loading: featureLoading } = useFeatureGate();

  const maxEnvironments = quotas.max_environments;
  const maxUsers = quotas.max_users;
  const canInvite = maxUsers > 1;

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [environments, setEnvironments] = useState<Env[]>([]);
  const [envLoading, setEnvLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [newEnvName, setNewEnvName] = useState("");

  // ── Consortium create + add-centre (cursor_logs/074 Phase G / 068 §3) ──────
  const [createConsortiumOpen, setCreateConsortiumOpen] = useState(false);
  const [newConsortiumName, setNewConsortiumName] = useState("");
  const [addCentreOpen, setAddCentreOpen] = useState(false);
  const [newCentreName, setNewCentreName] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editingEnv, setEditingEnv] = useState<Env | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingEnv, setDeletingEnv] = useState<Env | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  // The real number of patients the delete would destroy, learned from the server's
  // refusal (cursor_logs/068 G2). Null until the server tells us.
  const [deleteExclusiveCount, setDeleteExclusiveCount] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState<Env | null>(null);
  const [inviteRole, setInviteRole] = useState<string>("viewer");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);

  const [membersOpen, setMembersOpen] = useState(false);
  const [envMembers, setEnvMembers] = useState<EnvMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Joan, 2026-05-28: "People" view — users who share envs with the viewer.
  const [view, setView] = useState<"environments" | "people" | "consortium">("environments");

  // ── Consortium tab (cursor_logs/068 §6 P5) ──────────────────────────────
  // Read-only topology + "who can download this network", plus the cross-environment
  // grant/revoke/transfer actions. The topology itself needs no dedicated endpoint —
  // /environments/ already serves `kind` and `parent_consortium_env` — but the export
  // reach does, because it must be computed from the server-side resolver.
  const [consortiumData, setConsortiumData] = useState<ConsortiumOverview | null>(null);
  const [consortiumLoading, setConsortiumLoading] = useState(false);
  const [consortiumBusy, setConsortiumBusy] = useState(false);
  const [consortiumUser, setConsortiumUser] = useState("");
  const [consortiumRole, setConsortiumRole] = useState("viewer");
  const [transferFrom, setTransferFrom] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [consortiumInviteOpen, setConsortiumInviteOpen] = useState(false);
  const [people, setPeople] = useState<SharedUser[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  // Per-env chip action menu (role change / remove), gated by can_manage.
  const [chipMenu, setChipMenu] = useState<{
    el: HTMLElement;
    env: SharedUserEnv;
  } | null>(null);
  // "+ assign env" popover anchored to the + chip in a person's row, for
  // gatekeepers to add the listed user to another env they manage.
  const [assignMenu, setAssignMenu] = useState<{
    el: HTMLElement;
    person: SharedUser;
  } | null>(null);
  const [assignEnvId, setAssignEnvId] = useState<string | number | "">("");
  const [assignRole, setAssignRole] = useState<string>("viewer");
  const [assignLoading, setAssignLoading] = useState(false);

  const [backupOpen, setBackupOpen] = useState(false);
  const [backupRecords, setBackupRecords] = useState<any[]>([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restorePreview, setRestorePreview] = useState<any | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<string | null>(null);
  const [conflictMode, setConflictMode] = useState<"skip_shared" | "overwrite_shared">("skip_shared");

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  const totalPatients = useMemo(
    () => environments.reduce((acc, e) => acc + (e.patient_count || 0), 0),
    [environments],
  );

  const atLimit = useMemo(() => {
    if (maxEnvironments === 0) return false;
    return environments.length >= maxEnvironments;
  }, [environments.length, maxEnvironments]);

  const filteredEnvs = useMemo(() => {
    if (!searchQuery.trim()) return environments;
    const q = searchQuery.toLowerCase();
    return environments.filter((e) => e.name?.toLowerCase().includes(q));
  }, [environments, searchQuery]);

  // ── Consortium grouping (additive; cursor_logs/068) ──────────────────────
  // Nest member institutions under their consortium so the whole network can
  // collapse at once, without altering any per-env row action. Grouping is
  // derived purely from the serializer's `kind` + `parent_consortium_env`
  // (a child points at its consortium's id). While a search is active we fall
  // back to the flat list so matches surface immediately. Collapsed by default.
  const [expandedConsortia, setExpandedConsortia] = useState<Set<string>>(
    new Set(),
  );
  const toggleConsortium = useCallback((id: string) => {
    setExpandedConsortia((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  type EnvGroupMeta = {
    role: "header" | "child" | "flat";
    parentId?: string;
    childCount?: number;
    // True when this header row is a synthesized placeholder for a consortium the
    // user cannot see (memberless XXMM parent): render the fold but NO per-env
    // actions/counts, since there is no real environment behind it.
    synthesized?: boolean;
  };
  const { orderedEnvs, envGroupMeta } = useMemo(() => {
    const meta = new Map<string, EnvGroupMeta>();
    if (searchQuery.trim()) {
      for (const e of filteredEnvs) meta.set(String(e.id), { role: "flat" });
      return { orderedEnvs: filteredEnvs, envGroupMeta: meta };
    }
    const kindOf = (e: Env) => (e as Env & { kind?: string }).kind;
    const parentOf = (e: Env) =>
      (e as Env & { parent_consortium_env?: string | number | null })
        .parent_consortium_env;
    const parentNameOf = (e: Env) =>
      (e as Env & { parent_consortium_env_name?: string | null })
        .parent_consortium_env_name;
    const grouped = new Set<string>();
    const ordered: Env[] = [];

    // Real consortium envs the user can see, keyed by id.
    const consortiumById = new Map<string, Env>();
    for (const e of filteredEnvs) {
      if (kindOf(e) === "consortium") consortiumById.set(String(e.id), e);
    }
    // Institution envs bucketed by the consortium id they point at.
    const childrenByParent = new Map<string, Env[]>();
    for (const e of filteredEnvs) {
      if (kindOf(e) === "institution" && parentOf(e) != null) {
        const pid = String(parentOf(e));
        const arr = childrenByParent.get(pid) ?? [];
        arr.push(e);
        childrenByParent.set(pid, arr);
      }
    }
    // Union of every consortium id that is visible or referenced by a visible
    // child, in the order it first appears (stable rendering). Grouping keys off
    // the id the children point at, so a memberless consortium the user cannot
    // see still yields a fold header instead of a silent flat list.
    const consortiumOrder: string[] = [];
    const seenCid = new Set<string>();
    for (const e of filteredEnvs) {
      const cid =
        kindOf(e) === "consortium"
          ? String(e.id)
          : kindOf(e) === "institution" && parentOf(e) != null
            ? String(parentOf(e))
            : null;
      if (cid && !seenCid.has(cid)) {
        seenCid.add(cid);
        consortiumOrder.push(cid);
      }
    }

    for (const cid of consortiumOrder) {
      const children = childrenByParent.get(cid) ?? [];
      const real = consortiumById.get(cid);
      if (!real && children.length === 0) continue;
      let header: Env;
      let synthesized = false;
      if (real) {
        header = real;
      } else {
        synthesized = true;
        const name =
          parentNameOf(children[0]) ||
          (children[0]?.name.match(/^\s*(\[[^\]]+\])/)?.[1] ?? "") ||
          t("environments.kind_consortium", "consortium");
        header = { id: cid, name, kind: "consortium" } as Env & {
          kind?: string;
        };
      }
      meta.set(String(header.id), {
        role: "header",
        childCount: children.length,
        synthesized,
      });
      grouped.add(String(header.id));
      ordered.push(header);
      for (const ch of children) {
        meta.set(String(ch.id), { role: "child", parentId: String(cid) });
        grouped.add(String(ch.id));
        ordered.push(ch);
      }
    }
    for (const e of filteredEnvs) {
      if (!grouped.has(String(e.id))) {
        if (!meta.has(String(e.id))) meta.set(String(e.id), { role: "flat" });
        ordered.push(e);
      }
    }
    return { orderedEnvs: ordered, envGroupMeta: meta };
  }, [filteredEnvs, searchQuery, t]);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchEnvironments = useCallback(async () => {
    setEnvLoading(true);
    try {
      const res = await api.get("/environments/");
      setEnvironments(res.data || []);
    } catch (err) {
      logger.error("Error fetching environments:", err);
    } finally {
      setEnvLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnvironments();
  }, [fetchEnvironments]);

  useEffect(() => {
    if (backupOpen && selectedEnv) {
      (async () => {
        try {
          setBackupLoading(true);
          const res = await api.get(`/environments/${selectedEnv.id}/backups/`);
          setBackupRecords(res.data || []);
        } catch {
          setBackupRecords([]);
        } finally {
          setBackupLoading(false);
        }
      })();
    }
  }, [backupOpen, selectedEnv]);

  const fetchMembers = useCallback(async (envId: string) => {
    setMembersLoading(true);
    try {
      const res = await api.get(`/environments/${envId}/members/`);
      setEnvMembers(res.data || []);
    } catch (err) {
      logger.error("Error fetching members:", err);
      setEnvMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  // The consortium environment this user administers, if any. `kind` already ships on
  // every environment row, so the tab's visibility needs no extra request.
  const consortiumEnv = useMemo(
    () => environments.find((e) => (e as Env & { kind?: string }).kind === "consortium"),
    [environments],
  );

  const fetchConsortium = useCallback(async () => {
    if (!consortiumEnv) return;
    setConsortiumLoading(true);
    try {
      const res = await api.get(`/environments/${consortiumEnv.id}/consortium/overview/`);
      setConsortiumData(res.data);
    } catch (err) {
      logger.error("Error fetching consortium overview:", err);
      setConsortiumData(null);
    } finally {
      setConsortiumLoading(false);
    }
  }, [consortiumEnv]);

  // One helper for all three cross-environment actions: they share a shape (POST, then
  // refetch both the overview and the env list) and differ only in path and payload.
  const runConsortiumAction = useCallback(
    async (path: string, payload: Record<string, unknown>, successKey: string, successDefault: string) => {
      if (!consortiumEnv) return;
      setConsortiumBusy(true);
      try {
        await api.post(`/environments/${consortiumEnv.id}/consortium/${path}/`, payload);
        enqueueSnackbar(t(successKey, successDefault), { variant: "success" });
        await fetchConsortium();
      } catch (err: any) {
        enqueueSnackbar(
          err.response?.data?.error ||
            t("consortium.action_error", "The operation was refused and nothing was changed."),
          { variant: "error" },
        );
      } finally {
        setConsortiumBusy(false);
      }
    },
    [consortiumEnv, enqueueSnackbar, fetchConsortium, t],
  );

  // People view: users who share envs with the current user.
  const fetchPeople = useCallback(async () => {
    setPeopleLoading(true);
    try {
      const res = await api.get("/environments/shared-users/");
      setPeople(res.data || []);
    } catch (err) {
      logger.error("Error fetching shared users:", err);
      setPeople([]);
    } finally {
      setPeopleLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === "people") fetchPeople();
    if (view === "consortium") fetchConsortium();
  }, [view, fetchPeople, fetchConsortium]);

  const handlePeopleRoleChange = useCallback(
    async (env: SharedUserEnv, newRole: string) => {
      try {
        await api.patch(
          `/environments/${env.env_id}/members/${env.membership_id}/`,
          { role: newRole },
        );
        enqueueSnackbar(t("environments.role_updated", "Member role updated"), {
          variant: "success",
        });
        fetchPeople();
      } catch (err: any) {
        enqueueSnackbar(
          err.response?.data?.error ||
            t("environments.role_update_error", "Failed to update member"),
          { variant: "error" },
        );
      } finally {
        setChipMenu(null);
      }
    },
    [enqueueSnackbar, fetchPeople, t],
  );

  const handlePeopleRemove = useCallback(
    async (env: SharedUserEnv) => {
      try {
        await api.delete(
          `/environments/${env.env_id}/members/${env.membership_id}/remove/`,
        );
        enqueueSnackbar(t("environments.member_removed", "Member removed"), {
          variant: "success",
        });
        fetchPeople();
      } catch (err: any) {
        enqueueSnackbar(
          err.response?.data?.error ||
            t("environments.remove_error", "Failed to remove member"),
          { variant: "error" },
        );
      } finally {
        setChipMenu(null);
      }
    },
    [enqueueSnackbar, fetchPeople, t],
  );

  // Assign a listed user to an additional environment the gatekeeper manages.
  // Routes through /env-memberships/bulk/ (already idempotent + gatekeeper-gated
  // on the backend) so we don't introduce a second permission path.
  const handleAssignEnv = useCallback(async () => {
    if (!assignMenu || !assignEnvId) return;
    setAssignLoading(true);
    try {
      const res = await api.post("/env-memberships/bulk/", {
        rows: [
          {
            user_email: assignMenu.person.email,
            env_id: assignEnvId,
            role: assignRole || "viewer",
          },
        ],
      });
      // The bulk endpoint always returns HTTP 200 and folds per-row failures into
      // `errors[]`, so a 2xx does NOT mean the membership was created. Only report
      // success when a row was actually created or updated; otherwise surface the
      // per-row error instead of a misleading "Added" toast.
      const created = res.data?.created ?? 0;
      const updated = res.data?.updated ?? 0;
      const rowErrors: Array<{ message?: string }> = res.data?.errors ?? [];
      if (created + updated < 1) {
        const detail = rowErrors[0]?.message;
        enqueueSnackbar(
          detail
            ? `${t("environments.assign_error", "Failed to add to the environment.")} (${detail})`
            : t("environments.assign_error", "Failed to add to the environment."),
          { variant: "error" },
        );
        return;
      }
      enqueueSnackbar(
        t("environments.assign_success", "Added to the environment."),
        { variant: "success" },
      );
      setAssignMenu(null);
      setAssignEnvId("");
      setAssignRole("viewer");
      fetchPeople();
    } catch (err: any) {
      enqueueSnackbar(
        err.response?.data?.error ||
          t("environments.assign_error", "Failed to add to the environment."),
        { variant: "error" },
      );
    } finally {
      setAssignLoading(false);
    }
  }, [assignMenu, assignEnvId, assignRole, enqueueSnackbar, fetchPeople, t]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleCreate = useCallback(async () => {
    if (!newEnvName.trim()) {
      enqueueSnackbar(t("environments.name_required", "Environment name is required"), {
        variant: "warning",
      });
      return;
    }
    try {
      const res = await api.post("/environments/", { name: newEnvName.trim() });
      setEnvironments((prev) => [res.data, ...prev]);
      setNewEnvName("");
      setCreateOpen(false);
      enqueueSnackbar(t("environments.created_success", "Environment created successfully"), {
        variant: "success",
      });
    } catch (err: any) {
      const data = err.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        (data && typeof data === "object" ? Object.values(data).flat().join("; ") : null) ||
        t("environments.created_error", "Failed to create environment");
      enqueueSnackbar(String(msg), { variant: "error" });
    }
  }, [newEnvName, enqueueSnackbar, t]);

  // cursor_logs/074 Phase G / 068 §3: create a CONSORTIUM env. The server sets kind +
  // owner_organization and makes the caller its Owner; adding the result to
  // `environments` makes the Consortium tab appear (consortiumEnv is derived from it).
  const handleCreateConsortium = useCallback(async () => {
    if (!newConsortiumName.trim()) {
      enqueueSnackbar(t("consortium.name_required", "Consortium name is required"), {
        variant: "warning",
      });
      return;
    }
    try {
      const res = await api.post("/environments/consortium/create/", {
        name: newConsortiumName.trim(),
      });
      setEnvironments((prev) => [res.data, ...prev]);
      setNewConsortiumName("");
      setCreateConsortiumOpen(false);
      enqueueSnackbar(t("consortium.created_success", "Consortium created"), {
        variant: "success",
      });
    } catch (err: any) {
      const data = err.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        (data && typeof data === "object" ? Object.values(data).flat().join("; ") : null) ||
        t("consortium.created_error", "Failed to create consortium");
      enqueueSnackbar(String(msg), { variant: "error" });
    }
  }, [newConsortiumName, enqueueSnackbar, t]);

  // Add a centre (institution env) to the current consortium. The server parents it,
  // shares the consortium's owner_organization (single payer) and enforces the
  // per-centre owner-org cap; a non-manager is refused with a clear message.
  const handleAddCentre = useCallback(async () => {
    if (!consortiumEnv) return;
    if (!newCentreName.trim()) {
      enqueueSnackbar(t("consortium.centre_name_required", "Centre name is required"), {
        variant: "warning",
      });
      return;
    }
    try {
      await api.post(`/environments/${consortiumEnv.id}/consortium/add-centre/`, {
        name: newCentreName.trim(),
      });
      setNewCentreName("");
      setAddCentreOpen(false);
      enqueueSnackbar(t("consortium.centre_added", "Centre added to the network"), {
        variant: "success",
      });
      await fetchConsortium();
    } catch (err: any) {
      const data = err.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        (data && typeof data === "object" ? Object.values(data).flat().join("; ") : null) ||
        t("consortium.centre_add_error", "Failed to add centre");
      enqueueSnackbar(String(msg), { variant: "error" });
    }
  }, [consortiumEnv, newCentreName, enqueueSnackbar, fetchConsortium, t]);

  const handleUpdate = useCallback(async () => {
    if (!editingEnv) return;
    try {
      const res = await api.patch(`/environments/${editingEnv.id}/`, {
        name: editingEnv.name,
        allow_export: editingEnv.allow_export,
        allow_ai_extraction: editingEnv.allow_ai_extraction,
        require_patient_anonymization: editingEnv.require_patient_anonymization,
      });
      setEnvironments((prev) => prev.map((e) => (e.id === editingEnv.id ? res.data : e)));
      setEditOpen(false);
      setEditingEnv(null);
      enqueueSnackbar(t("environments.updated_success", "Environment updated"), {
        variant: "success",
      });
    } catch (err: any) {
      enqueueSnackbar(
        err.response?.data?.error ||
          t("environments.updated_error", "Failed to update environment"),
        { variant: "error" },
      );
    }
  }, [editingEnv, enqueueSnackbar, t]);

  const handleDelete = useCallback(async () => {
    if (!deletingEnv) return;
    setDeleteLoading(true);
    try {
      // cursor_logs/068 G2: the server now refuses the delete and reports how many
      // patients exist in NO other environment — the ones that would actually be
      // destroyed. That is not `patient_count`, which counts every patient in the env
      // (~25k for a consortium). Echo the real number back to confirm.
      const qs = deleteExclusiveCount !== null ? `?confirm_patients=${deleteExclusiveCount}` : "";
      await api.delete(`/environments/${deletingEnv.id}/${qs}`);
      setEnvironments((prev) => prev.filter((e) => e.id !== deletingEnv.id));
      setDeleteOpen(false);
      setDeletingEnv(null);
      setDeleteConfirmText(""); setDeleteExclusiveCount(null);
      enqueueSnackbar(t("environments.deleted_success", "Environment deleted"), {
        variant: "success",
      });
    } catch (err: any) {
      // The exclusive-patient guard replies with the true count. Keep the dialog open,
      // show the real number, and let the next click confirm it.
      const exclusive = err.response?.data?.exclusive_patient_count;
      if (typeof exclusive === "number") {
        setDeleteExclusiveCount(exclusive);
      }
      enqueueSnackbar(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          t("environments.deleted_error", "Failed to delete environment"),
        { variant: "error" },
      );
    } finally {
      setDeleteLoading(false);
    }
  }, [deletingEnv, deleteExclusiveCount, enqueueSnackbar, t]);

  const handleInvite = useCallback(async () => {
    if (!selectedEnv || !inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      await api.post(`/environments/${selectedEnv.id}/invite/`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInviteEmail("");
      setInviteRole("viewer");
      setInviteOpen(false);
      enqueueSnackbar(
        t("environments.invite_sent", "Invitation sent successfully"),
        { variant: "success" },
      );
    } catch (err: any) {
      enqueueSnackbar(
        err.response?.data?.error || t("environments.invite_error", "Failed to send invitation"),
        { variant: "error" },
      );
    } finally {
      setInviteLoading(false);
    }
  }, [selectedEnv, inviteEmail, inviteRole, enqueueSnackbar, t]);

  // Joan, 2026-05-28: inline "add user" at the bottom of the members table
  // (no separate dialog). Reuses the invite endpoint and refreshes the
  // members list in place.
  const handleInlineAdd = useCallback(async () => {
    if (!selectedEnv || !inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      await api.post(`/environments/${selectedEnv.id}/invite/`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInviteEmail("");
      setInviteRole("viewer");
      enqueueSnackbar(
        t("environments.invite_sent", "Invitation sent successfully"),
        { variant: "success" },
      );
      fetchMembers(selectedEnv.id);
    } catch (err: any) {
      enqueueSnackbar(
        err.response?.data?.error ||
          t("environments.invite_error", "Failed to send invitation"),
        { variant: "error" },
      );
    } finally {
      setInviteLoading(false);
    }
  }, [selectedEnv, inviteEmail, inviteRole, enqueueSnackbar, fetchMembers, t]);

  const handleUpdateMemberRole = useCallback(
    async (envId: string, memberId: number, newRole: string) => {
      try {
        await api.patch(`/environments/${envId}/members/${memberId}/`, { role: newRole });
        enqueueSnackbar(t("environments.role_updated", "Member role updated"), {
          variant: "success",
        });
        fetchMembers(envId);
      } catch (err: any) {
        enqueueSnackbar(
          err.response?.data?.error ||
            t("environments.role_update_error", "Failed to update member"),
          { variant: "error" },
        );
      }
    },
    [enqueueSnackbar, fetchMembers, t],
  );

  // Per-member export permission toggle. PATCHes just `{ can_export }`
  // (the UpdateMemberSerializer already accepts it) and updates the row
  // optimistically, reverting on failure. Mirrors handleUpdateMemberRole's
  // request shape but keeps the switch snappy instead of refetching.
  const handleUpdateMemberExport = useCallback(
    async (envId: string, memberId: number, canExport: boolean) => {
      setEnvMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, can_export: canExport } : m)),
      );
      try {
        await api.patch(`/environments/${envId}/members/${memberId}/`, {
          can_export: canExport,
        });
        enqueueSnackbar(
          t("environments.export_updated", "Export permission updated"),
          { variant: "success" },
        );
      } catch (err: any) {
        setEnvMembers((prev) =>
          prev.map((m) =>
            m.id === memberId ? { ...m, can_export: !canExport } : m,
          ),
        );
        enqueueSnackbar(
          err.response?.data?.error ||
            t(
              "environments.export_update_error",
              "Failed to update export permission",
            ),
          { variant: "error" },
        );
      }
    },
    [enqueueSnackbar, t],
  );

  const handleRemoveMember = useCallback(
    async (envId: string, memberId: number) => {
      try {
        await api.delete(`/environments/${envId}/members/${memberId}/remove/`);
        enqueueSnackbar(t("environments.member_removed", "Member removed"), {
          variant: "success",
        });
        fetchMembers(envId);
      } catch (err: any) {
        enqueueSnackbar(
          err.response?.data?.error ||
            t("environments.member_remove_error", "Failed to remove member"),
          { variant: "error" },
        );
      }
    },
    [enqueueSnackbar, fetchMembers, t],
  );

  // ---------------------------------------------------------------------------
  // Reusable style references (copied from Dashboard / CohortSummaryCards)
  // ---------------------------------------------------------------------------

  // Dark-glass surface (cursor_logs/069): `theme.custom.contentText.*` resolves to
  // deepNavy / blueGray — correct ink for a light card, invisible on this one. Every
  // text colour on the page flows through these two, so re-pointing them at the
  // BulkUploadResults values re-colours the page consistently instead of piecemeal.
  const textPrimary = athenaColors.white;
  const textSecondary = alpha(athenaColors.white, 0.65);

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  if (envLoading || featureLoading) {
    return (
      <Container disableGutters maxWidth={false} sx={{ flex: 1, p: { md: 0.5, lg: 2,  xl: 3 }, overflow: "auto" }}>
        <Skeleton variant="rectangular" height={48} sx={{ mb: 3, borderRadius: 2 }} />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={90} sx={{ flex: "1 1 0", minWidth: 160, borderRadius: 2 }} />
          ))}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
          {[1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      </Container>
    );
  }

  // ---------------------------------------------------------------------------
  // Stat card (matches CohortSummaryCards pattern)
  // ---------------------------------------------------------------------------

  const StatCard: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string | number;
    subtitle?: string;
    color: string;
  }> = ({ icon, label, value, subtitle, color }) => (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: { xs: 130, sm: 160 },
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: 2,
        background: alpha(athenaColors.deepNavy, 0.35),
        border: `1px solid ${alpha(athenaColors.white, 0.1)}`,
        backdropFilter: "blur(8px)",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        transition: "all 0.2s ease",
        "&:hover": {
          border: `1px solid ${alpha(color, 0.55)}`,
          transform: "translateY(-2px)",
          boxShadow: `0 4px 20px ${alpha(athenaColors.deepNavy, 0.45)}`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: alpha(color, 0.18),
            border: `1px solid ${alpha(color, 0.35)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: textSecondary,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontSize: "0.65rem",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 800, color: textPrimary, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: textSecondary, fontSize: "0.7rem" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Toggle row for edit dialog (dark variant, chip under the toggle)
  // ---------------------------------------------------------------------------

  const ToggleRow: React.FC<{
    checked: boolean;
    onChange: (checked: boolean) => void;
    label: string;
    description: string;
  }> = ({ checked, onChange, label, description }) => (
    <Box sx={{ mb: 2.5, display: "flex", gap: 2, alignItems: "flex-start" }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, pt: 0.25 }}>
        <Switch
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          sx={{
            "& .MuiSwitch-track": {
              backgroundColor: alpha(theme.palette.common.white, 0.2),
            },
            "& .Mui-checked + .MuiSwitch-track": {
              backgroundColor: `${alpha(athenaColors.periwinkle, 0.5)} !important`,
            },
          }}
        />
        <Chip
          label={checked
            ? t("environments.toggle_active", "Active")
            : t("environments.toggle_disabled", "Disabled")}
          size="small"
          sx={{
            fontSize: "0.65rem",
            height: 18,
            bgcolor: checked ? alpha(athenaColors.periwinkle, 0.25) : alpha(theme.palette.common.white, 0.1),
            color: checked ? athenaColors.periwinkle : alpha(theme.palette.common.white, 0.5),
            border: `1px solid ${checked ? alpha(athenaColors.periwinkle, 0.4) : alpha(theme.palette.common.white, 0.15)}`,
          }}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.common.white }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: athenaColors.periwinkle, lineHeight: 1.4 }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        p: { md: 0.5, lg: 2,  xl: 3 },
        minHeight: 0,
      }}
    >
      {/* The page adopts the WelcomeHeroCard / BulkUploadResults chrome: looping video,
          navy gradient, frosted glass card. VideoGlassContainer also injects `darkOverrides`,
          which re-colours every MUI child for a dark surface. */}
      <VideoGlassContainer
        variant="dark"
        glassSx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2, md: 3 },
          // Required, not cosmetic. The glass card is a flex COLUMN with a max height,
          // so by default every child gets flex-shrink:1 and is squeezed to fit —
          // which silently clipped the last row of the roles matrix ("Viewer") and
          // truncated the environments table. WelcomeHeroCard carries the same guard.
          "& > *": { flexShrink: 0 },
        }}
      >
        {/* ── Header ────────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: textPrimary }} gutterBottom>
              {t("environment.manage_title", "Environments")}
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t(
                "environments.page_description",
                "Manage your environments, invite team members, and control access to patient data.",
              )}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder={t("environment.search_label", "Search...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 0.5, color: textSecondary, fontSize: 20 }} />,
              }}
              sx={{
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: alpha(athenaColors.deepNavy, 0.35),
                  backdropFilter: "blur(8px)",
                },
              }}
            />
            {atLimit ? (
              <Tooltip
                title={t(
                  "tier_gate.env_limit_reached",
                  "You have reached the maximum number of environments for your current plan.",
                )}
                arrow
              >
                <span>
                  <Button variant="contained" startIcon={<AddIcon />} disabled sx={{ textTransform: "none" }}>
                    {t("environments.create_environment", "Create Environment")}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{
                  textTransform: "none",
                  background: `linear-gradient(180deg, ${athenaColors.royalBlue} 0%, ${alpha(athenaColors.royalBlue, 0.82)} 100%)`,
                  border: `1px solid ${alpha(athenaColors.periwinkle, 0.4)}`,
                  boxShadow: theme.custom?.glassShadow,
                  backdropFilter: "blur(6px)",
                  "&:hover": {
                    background: `linear-gradient(180deg, ${alpha(athenaColors.royalBlue, 1)} 0%, ${athenaColors.royalBlue} 100%)`,
                    borderColor: alpha(athenaColors.periwinkle, 0.6),
                  },
                }}
              >
                {t("environments.create_environment", "Create Environment")}
              </Button>
            )}
            {!atLimit && (
              <Button
                variant="outlined"
                startIcon={<HubIcon />}
                onClick={() => setCreateConsortiumOpen(true)}
                sx={{
                  textTransform: "none",
                  ml: 1,
                  color: athenaColors.periwinkle,
                  borderColor: alpha(athenaColors.periwinkle, 0.4),
                  "&:hover": {
                    borderColor: athenaColors.periwinkle,
                    bgcolor: alpha(athenaColors.periwinkle, 0.08),
                  },
                }}
              >
                {t("consortium.create_consortium", "Create Consortium")}
              </Button>
            )}
          </Box>
        </Box>

        {/* ── Environments | People toggle (Joan, 2026-05-28) ────── */}
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(_e, v) => {
            if (v) setView(v);
          }}
          sx={{
            alignSelf: "flex-start",
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 600,
              px: 2,
              color: textSecondary,
              borderColor: alpha(athenaColors.white, 0.18),
              "&.Mui-selected": {
                color: athenaColors.white,
                bgcolor: alpha(athenaColors.periwinkle, 0.18),
                borderColor: alpha(athenaColors.periwinkle, 0.45),
                "&:hover": { bgcolor: alpha(athenaColors.periwinkle, 0.26) },
              },
            },
          }}
        >
          <ToggleButton value="environments">
            <FolderIcon sx={{ fontSize: 16, mr: 0.75 }} />
            {t("environments.toggle_environments", "Environments")}
          </ToggleButton>
          <ToggleButton value="people">
            <PeopleAltIcon sx={{ fontSize: 16, mr: 0.75 }} />
            {t("environments.toggle_people", "People")}
          </ToggleButton>
          {/* Only surfaced to someone who actually administers a network. */}
          {consortiumEnv && (
            <ToggleButton value="consortium">
              <HubIcon sx={{ fontSize: 16, mr: 0.75 }} />
              {t("environments.toggle_consortium", "Consortium")}
            </ToggleButton>
          )}
        </ToggleButtonGroup>

        {/* ── Stats row (CohortSummaryCards pattern) — env view only ─ */}
        {view === "environments" && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <StatCard
            icon={<FolderIcon />}
            label={t("environments.stat_environments", "ENVIRONMENTS")}
            value={environments.length}
            subtitle={
              maxEnvironments > 0
                ? `of ${maxEnvironments} on ${tier.display_name} plan`
                : `Unlimited on ${tier.display_name} plan`
            }
            color={athenaColors.periwinkle}
          />
          <StatCard
            icon={<PeopleAltIcon />}
            label={t("environments.stat_team_seats", "TEAM SEATS")}
            value={maxUsers === 0 ? t("environments.unlimited", "Unlimited") : String(maxUsers)}
            subtitle={`${tier.display_name} plan`}
            color={athenaColors.lightPurple}
          />
          <StatCard
            icon={<StorageIcon />}
            label={t("environments.stat_total_patients", "TOTAL PATIENTS")}
            value={totalPatients}
            subtitle={t("environments.stat_across_envs", "Across all environments")}
            color={athenaColors.lightOrange}
          />
        </Box>
        )}

        {/* ── Roles reference — shown in BOTH the Environments and People tabs.
            What each environment role can do. English now; i18n keys carry English
            defaults (requires_translation) and are translated at the final i18n pass. */}
        {(view === "environments" || view === "people") && (
          <TableContainer
            sx={{
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${alpha(athenaColors.white, 0.12)}`,
              background: alpha(athenaColors.deepNavy, 0.35),
              backdropFilter: "blur(8px)",
              overflowX: "auto",
            }}
          >
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textPrimary }}>
                {t("environments.roles.title", "What each role can do")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: textSecondary, fontSize: "0.68rem", lineHeight: 1.4 }}
              >
                {t(
                  "environments.roles.export_note_short",
                  "Export also needs the environment's export switch on. A consortium has it off: nobody downloads network-wide, though view and edit are unaffected."
                )}
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    t("environments.roles.col_role", "Role"),
                    t("environments.roles.col_view", "View"),
                    t("environments.roles.col_add", "Add"),
                    t("environments.roles.col_edit", "Edit"),
                    t("environments.roles.col_delete", "Delete"),
                    t("environments.roles.col_export", "Export / download"),
                    t("environments.roles.col_manage", "Manage members"),
                  ].map((h, i) => (
                    <TableCell
                      key={i}
                      align={i === 0 ? "left" : "center"}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        color: textPrimary,
                        whiteSpace: "nowrap",
                        py: 0.6,
                        borderBottom: `1px solid ${alpha(athenaColors.white, 0.15)}`,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { role: t("environments.roles.owner", "Owner"), caps: [1, 1, 1, 1, 1, 1] },
                  { role: t("environments.roles.admin", "Admin"), caps: [1, 1, 1, 1, 1, 1] },
                  { role: t("environments.roles.editor", "Editor"), caps: [1, 1, 1, 0, 1, 0] },
                  { role: t("environments.roles.contributor", "Contributor"), caps: [1, 1, 0, 0, 0, 0] },
                  { role: t("environments.roles.viewer", "Viewer"), caps: [1, 0, 0, 0, 0, 0] },
                ].map((r) => (
                  <TableRow key={r.role}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.74rem",
                        color: textPrimary,
                        py: 0.4,
                        borderBottom: `1px solid ${alpha(athenaColors.white, 0.06)}`,
                      }}
                    >
                      {r.role}
                    </TableCell>
                    {r.caps.map((c, i) => (
                      <TableCell
                        key={i}
                        align="center"
                        sx={{
                          fontSize: "0.85rem",
                          py: 0.4,
                          color: c
                            ? theme.palette.success.light
                            : alpha(athenaColors.white, 0.35),
                          borderBottom: `1px solid ${alpha(athenaColors.white, 0.06)}`,
                        }}
                      >
                        {c ? "✓" : "—"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── People view ──────────────────────────────────────────── */}
        {/* ── Consortium tab (cursor_logs/068 §6 P5) ───────────────────── */}
        {view === "consortium" && (
          consortiumLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : !consortiumData ? (
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t("consortium.unavailable", "Consortium details are unavailable.")}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <StatCard
                  icon={<HubIcon />}
                  label={t("consortium.stat_centres", "CENTRES")}
                  value={consortiumData.centres.length}
                  subtitle={consortiumData.consortium.name || ""}
                  color={athenaColors.periwinkle}
                />
                <StatCard
                  icon={<StorageIcon />}
                  label={t("consortium.stat_patients", "PATIENTS")}
                  value={consortiumData.centres.reduce((n, c) => n + (c.patient_count || 0), 0)}
                  subtitle={t("consortium.stat_across_centres", "Across all centres")}
                  color={athenaColors.lightOrange}
                />
                <StatCard
                  icon={<PeopleAltIcon />}
                  label={t("consortium.stat_network_reach", "REACH THE WHOLE NETWORK")}
                  value={consortiumData.network_reach.filter((r) => r.reaches_whole_network).length}
                  subtitle={t("consortium.stat_of_people", "of {n} people").replace(
                    "{n}",
                    String(consortiumData.network_reach.length),
                  )}
                  color={athenaColors.orange}
                />
              </Box>

              {/* Topology — the only place the parent/child relation is visible. */}
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 700 }}>
                    {t("consortium.topology_title", "Environments in this network")}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    disabled={consortiumBusy}
                    onClick={() => setAddCentreOpen(true)}
                    sx={{
                      textTransform: "none",
                      color: athenaColors.periwinkle,
                      borderColor: alpha(athenaColors.periwinkle, 0.4),
                      "&:hover": {
                        borderColor: athenaColors.periwinkle,
                        bgcolor: alpha(athenaColors.periwinkle, 0.08),
                      },
                    }}
                  >
                    {t("consortium.add_centre", "Add centre")}
                  </Button>
                </Box>
                <TableContainer
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(athenaColors.white, 0.12)}`,
                    background: alpha(athenaColors.deepNavy, 0.35),
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {[
                          t("consortium.col_environment", "Environment"),
                          t("consortium.col_patients", "Patients"),
                          t("consortium.col_members", "Members"),
                          t("consortium.col_download", "Download"),
                        ].map((h, i) => (
                          <TableCell key={i} sx={{ fontWeight: 700, fontSize: "0.74rem", color: textPrimary }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ color: textPrimary, fontWeight: 700 }}>
                          {consortiumData.consortium.name}{" "}
                          <Chip
                            label={t("environments.kind_consortium", "consortium")}
                            size="small"
                            sx={{ height: 18, fontSize: "0.62rem" }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: textSecondary }}>&mdash;</TableCell>
                        <TableCell sx={{ color: textSecondary }}>&mdash;</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              consortiumData.consortium.allow_export
                                ? t("consortium.download_on", "On")
                                : t("consortium.download_off", "Off")
                            }
                            size="small"
                            color={consortiumData.consortium.allow_export ? "success" : "default"}
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                        </TableCell>
                      </TableRow>
                      {consortiumData.centres.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell sx={{ color: textPrimary, pl: 3 }}>{c.name}</TableCell>
                          <TableCell sx={{ color: textSecondary }}>{c.patient_count}</TableCell>
                          <TableCell sx={{ color: textSecondary }}>{c.members_count}</TableCell>
                          <TableCell>
                            <Chip
                              label={
                                c.allow_export
                                  ? t("consortium.download_on", "On")
                                  : t("consortium.download_off", "Off")
                              }
                              size="small"
                              color={c.allow_export ? "success" : "default"}
                              variant="outlined"
                              sx={{ height: 20, fontSize: "0.65rem" }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Network reach — the answer to "who can download this network". */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 700, mb: 0.5 }}>
                  {t("consortium.reach_title", "Who can download this network")}
                </Typography>
                <Typography variant="caption" sx={{ color: textSecondary, display: "block", mb: 1 }}>
                  {t(
                    "consortium.reach_hint",
                    "Membership of the consortium grants view and edit, never download. Download reach comes only from membership of the individual centres.",
                  )}
                </Typography>
                <TableContainer
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(athenaColors.white, 0.12)}`,
                    background: alpha(athenaColors.deepNavy, 0.35),
                    maxHeight: 320,
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {[
                          t("environments.col_user", "User"),
                          t("consortium.col_centres_downloadable", "Centres they can download"),
                        ].map((h, i) => (
                          <TableCell key={i} sx={{ fontWeight: 700, fontSize: "0.74rem", color: textPrimary }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {consortiumData.network_reach.map((r) => (
                        <TableRow key={r.user_id}>
                          <TableCell sx={{ color: textPrimary }}>{r.email}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" sx={{ color: textSecondary }}>
                                {r.exportable_env_ids.length} / {consortiumData.centres.length}
                              </Typography>
                              {r.reaches_whole_network && (
                                <Chip
                                  label={t("consortium.whole_network", "Whole network")}
                                  size="small"
                                  color="warning"
                                  sx={{ height: 20, fontSize: "0.65rem" }}
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Cross-environment actions. */}
              <Box
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${alpha(athenaColors.white, 0.12)}`,
                  background: alpha(athenaColors.deepNavy, 0.35),
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 700, mb: 0.5 }}>
                  {t("consortium.actions_title", "Access across the whole network")}
                </Typography>
                <Typography variant="caption" sx={{ color: textSecondary, display: "block", mb: 1.5 }}>
                  {t(
                    "consortium.actions_hint",
                    "These apply to the consortium and every centre at once. Removing someone only from the consortium would leave their access to each centre, and therefore their download of the whole network, intact.",
                  )}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setConsortiumInviteOpen(true)}
                  sx={{ textTransform: "none", mb: 1.5 }}
                >
                  {t("consortium.invite_open", "Invite a member")}
                </Button>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  <TextField
                    size="small"
                    placeholder={t("consortium.user_placeholder", "user@hospital.org")}
                    value={consortiumUser}
                    onChange={(e) => setConsortiumUser(e.target.value)}
                    sx={{ minWidth: 240 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>{t("environments.role_label", "Role")}</InputLabel>
                    <Select
                      value={consortiumRole}
                      label={t("environments.role_label", "Role")}
                      onChange={(e) => setConsortiumRole(e.target.value as string)}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <MenuItem key={r.value} value={r.value}>
                          {r.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    disabled={!consortiumUser || consortiumBusy}
                    onClick={() =>
                      runConsortiumAction(
                        "grant",
                        { user: consortiumUser, role: consortiumRole },
                        "consortium.granted",
                        "Access granted across the network",
                      )
                    }
                    sx={{ textTransform: "none" }}
                  >
                    {t("consortium.grant", "Grant across network")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={!consortiumUser || consortiumBusy}
                    onClick={() =>
                      runConsortiumAction(
                        "revoke",
                        { user: consortiumUser },
                        "consortium.revoked",
                        "Access revoked across the network",
                      )
                    }
                    sx={{ textTransform: "none" }}
                  >
                    {t("consortium.revoke", "Revoke everywhere")}
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 700, mb: 1 }}>
                  {t("consortium.transfer_title", "Transfer the network lead")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  <TextField
                    size="small"
                    label={t("consortium.transfer_from", "From")}
                    value={transferFrom}
                    onChange={(e) => setTransferFrom(e.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    size="small"
                    label={t("consortium.transfer_to", "To")}
                    value={transferTo}
                    onChange={(e) => setTransferTo(e.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                  <Button
                    variant="contained"
                    disabled={!transferFrom || !transferTo || consortiumBusy}
                    onClick={() =>
                      runConsortiumAction(
                        "transfer-lead",
                        { from_user: transferFrom, to_user: transferTo },
                        "consortium.transferred",
                        "Network lead transferred",
                      )
                    }
                    sx={{ textTransform: "none" }}
                  >
                    {t("consortium.transfer", "Transfer")}
                  </Button>
                </Box>
              </Box>
            </Box>
          )
        )}

        {consortiumEnv && (
          <InviteToConsortiumDialog
            open={consortiumInviteOpen}
            onClose={() => setConsortiumInviteOpen(false)}
            consortiumEnvId={String(consortiumEnv.id)}
            consortiumName={consortiumData?.consortium.name || consortiumEnv.name}
            centres={consortiumData?.centres.map((c) => ({ id: String(c.id), name: c.name }))}
            onInvited={fetchConsortium}
          />
        )}

        {view === "people" && (
          <>
          {peopleLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : people.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 6,
                px: 3,
                borderRadius: 2,
                background: alpha(athenaColors.deepNavy, 0.35),
                border: `1px solid ${alpha(athenaColors.white, 0.12)}`,
                backdropFilter: "blur(8px)",
              }}
            >
              <PeopleAltIcon sx={{ fontSize: 56, color: textSecondary, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" sx={{ color: textPrimary }}>
                {t("environments.no_people", "No one shares an environment with you yet")}
              </Typography>
            </Box>
          ) : (
            <TableContainer
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(athenaColors.white, 0.12)}`,
                background: alpha(athenaColors.deepNavy, 0.35),
                backdropFilter: "blur(8px)",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      t("environments.col_user", "User"),
                      t("environments.col_shared_envs", "Shared environments"),
                    ].map((h, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.74rem",
                          color: textPrimary,
                          background: alpha(athenaColors.deepNavy, 0.85),
                          backdropFilter: "blur(8px)",
                          borderBottom: `1px solid ${alpha(athenaColors.white, 0.15)}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {people.map((person) => {
                    const name =
                      `${person.first_name || ""} ${person.last_name || ""}`.trim() ||
                      person.username ||
                      person.email;
                    const initials = (person.first_name?.[0] || person.email[0]).toUpperCase();
                    const cellSx = {
                      color: textPrimary,
                      borderBottom: `1px solid ${alpha(athenaColors.white, 0.08)}`,
                      verticalAlign: "top" as const,
                      py: 1,
                    };
                    return (
                      <TableRow
                        key={person.id}
                        hover
                        sx={{ "&:hover": { background: alpha(athenaColors.periwinkle, 0.08) } }}
                      >
                        <TableCell sx={cellSx}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                fontSize: "0.78rem",
                                bgcolor: alpha(athenaColors.periwinkle, 0.2),
                                color: athenaColors.periwinkle,
                              }}
                            >
                              {initials}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ color: textPrimary }}>
                                {name}
                              </Typography>
                              <Typography variant="caption" noWrap sx={{ color: textSecondary, display: "block" }}>
                                {person.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {person.environments.map((env) => (
                              <Chip
                                key={env.membership_id}
                                size="small"
                                label={`${env.env_name} · ${env.role}`}
                                onClick={
                                  env.can_manage
                                    ? (e) => setChipMenu({ el: e.currentTarget, env })
                                    : undefined
                                }
                                onDelete={
                                  env.can_manage ? () => handlePeopleRemove(env) : undefined
                                }
                                deleteIcon={
                                  env.can_manage ? (
                                    <Tooltip title={t("environments.remove_from_env", "Remove from this environment")}>
                                      <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                                    </Tooltip>
                                  ) : undefined
                                }
                                variant="outlined"
                                sx={{
                                  maxWidth: 280,
                                  fontSize: "0.7rem",
                                  cursor: env.can_manage ? "pointer" : "default",
                                  borderColor: alpha(athenaColors.white, 0.18),
                                  "& .MuiChip-label": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  },
                                }}
                              />
                            ))}
                            {/* "+ assign env" chip — opens a popover for the
                                gatekeeper to add this user to another env
                                they manage. Hidden if the gatekeeper has no
                                envs at all (then there's nothing to assign). */}
                            {environments.length > 0 && (
                              <Tooltip
                                title={t(
                                  "environments.assign_tooltip",
                                  "Add to an environment you manage",
                                )}
                                arrow
                              >
                                <Chip
                                  icon={<AddIcon sx={{ fontSize: 16 }} />}
                                  label={t("environments.assign_chip", "Add")}
                                  size="small"
                                  onClick={(e) => {
                                    setAssignEnvId("");
                                    setAssignRole("viewer");
                                    setAssignMenu({
                                      el: e.currentTarget,
                                      person,
                                    });
                                  }}
                                  sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    borderStyle: "dashed",
                                    borderColor: alpha(
                                      athenaColors.periwinkle,
                                      0.5,
                                    ),
                                    color: athenaColors.periwinkle,
                                    bgcolor: "transparent",
                                    "& .MuiChip-icon": {
                                      color: athenaColors.periwinkle,
                                    },
                                    "&:hover": {
                                      bgcolor: alpha(
                                        athenaColors.periwinkle,
                                        0.08,
                                      ),
                                    },
                                  }}
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </>
        )}

        {/* Per-env chip action menu (role change). Removal is the chip's X. */}
        <Menu
          anchorEl={chipMenu?.el ?? null}
          open={Boolean(chipMenu)}
          onClose={() => setChipMenu(null)}
        >
          <Typography
            variant="caption"
            sx={{ px: 2, py: 0.5, display: "block", color: textSecondary, fontWeight: 700 }}
          >
            {t("environments.change_role", "Change role")}
          </Typography>
          {ROLE_OPTIONS.map((r) => (
            <MenuItem
              key={r.value}
              selected={chipMenu?.env.role === r.value}
              onClick={() => {
                if (chipMenu) handlePeopleRoleChange(chipMenu.env, r.value);
              }}
              sx={{ fontSize: "0.85rem" }}
            >
              {t(`manage_env.role_${r.value}`, r.label)}
            </MenuItem>
          ))}
          <Divider />
          <MenuItem
            onClick={() => {
              if (chipMenu) handlePeopleRemove(chipMenu.env);
            }}
            sx={{ fontSize: "0.85rem", color: theme.palette.error.main }}
          >
            <RemoveCircleOutlineIcon sx={{ fontSize: 16, mr: 1 }} />
            {t("environments.remove_from_env", "Remove from this environment")}
          </MenuItem>
        </Menu>

        {/* + assign env Popover — adds the listed person to another env the
            gatekeeper manages, via /env-memberships/bulk/ (idempotent on the
            backend, so a row that already exists just updates the role). */}
        <Popover
          anchorEl={assignMenu?.el ?? null}
          open={Boolean(assignMenu)}
          onClose={() => setAssignMenu(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Box sx={{ p: 2, width: 320, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 700 }}>
              {t("environments.assign_title", "Add to environment")}
            </Typography>
            <Autocomplete
              size="small"
              options={(() => {
                if (!assignMenu) return [];
                const already = new Set(
                  assignMenu.person.environments.map((e) => String(e.env_id)),
                );
                return environments.filter((e) => !already.has(String(e.id)));
              })()}
              getOptionLabel={(o) => o.name || `Environment #${o.id}`}
              isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
              value={
                environments.find((e) => String(e.id) === String(assignEnvId)) || null
              }
              onChange={(_, v) => setAssignEnvId(v ? v.id : "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("environments.assign_env_label", "Environment")}
                  placeholder={t(
                    "environments.assign_env_placeholder",
                    "Pick an environment…",
                  )}
                />
              )}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{t("environments.assign_role_label", "Role")}</InputLabel>
              <Select
                value={assignRole}
                label={t("environments.assign_role_label", "Role")}
                onChange={(e) => setAssignRole(e.target.value as string)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {t(`manage_env.role_${r.value}`, r.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}>
              <Button
                size="small"
                onClick={() => setAssignMenu(null)}
                disabled={assignLoading}
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleAssignEnv}
                disabled={!assignEnvId || assignLoading}
                startIcon={
                  assignLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <AddIcon />
                  )
                }
              >
                {t("environments.assign_confirm", "Add")}
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* ── Environment table — env view only ───────────────────── */}
        {view === "environments" && (
        filteredEnvs.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              px: 3,
              borderRadius: 2,
              background: alpha(athenaColors.deepNavy, 0.35),
              border: `1px solid ${alpha(athenaColors.white, 0.12)}`,
              backdropFilter: "blur(8px)",
            }}
          >
            <FolderIcon sx={{ fontSize: 56, color: textSecondary, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" sx={{ color: textPrimary }} gutterBottom>
              {environments.length === 0
                ? t("environments.no_environments", "No environments yet")
                : t("environments.no_results", "No environments match your search")}
            </Typography>
            {environments.length === 0 && (
              <>
                <Typography variant="body2" sx={{ color: textSecondary, mb: 3, maxWidth: 480, mx: "auto" }}>
                  {t(
                    "environments.no_environments_desc",
                    "Create your first environment to start organizing patient data.",
                  )}
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} sx={{ textTransform: "none" }}>
                  {t("environments.create_environment", "Create Environment")}
                </Button>
              </>
            )}
          </Box>
        ) : (
          /* ── Environments table (Joan, 2026-05-28: list instead of cards —
              scales to 70+ envs). Same table pattern as the members table,
              styled for this page's light glass surface. ─────────────── */
          <TableContainer
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(athenaColors.white, 0.12)}`,
              background: alpha(athenaColors.deepNavy, 0.35),
              backdropFilter: "blur(8px)",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    t("environments.col_name", "Environment"),
                    t("environments.col_patients", "Patients"),
                    t("environments.col_members", "Members"),
                    t("environments.col_features", "Features"),
                    t("environments.col_billed", "Billed to"),
                    "",
                  ].map((h, i) => (
                    <TableCell
                      key={i}
                      align={i === 1 || i === 2 ? "right" : i === 5 ? "right" : "left"}
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.74rem",
                        color: textPrimary,
                        background: alpha(athenaColors.deepNavy, 0.85),
                        backdropFilter: "blur(8px)",
                        borderBottom: `1px solid ${alpha(athenaColors.white, 0.15)}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {orderedEnvs.map((env) => {
                  const cellSx = {
                    color: textPrimary,
                    borderBottom: `1px solid ${alpha(athenaColors.white, 0.08)}`,
                    fontSize: "0.82rem",
                  };
                  const kind = (env as Env & { kind?: string }).kind;
                  // Consortium grouping: header rows carry a collapse chevron;
                  // child rows are indented and hidden while their consortium is
                  // collapsed. Row actions themselves are untouched.
                  const meta = envGroupMeta.get(String(env.id));
                  const isHeader = meta?.role === "header";
                  const isChild = meta?.role === "child";
                  // A synthesized header stands in for a consortium the user cannot
                  // see: it has no real env behind it, so its per-env counts and
                  // row actions are suppressed.
                  const isSynthesized = meta?.synthesized === true;
                  const expanded = isHeader
                    ? expandedConsortia.has(String(env.id))
                    : false;
                  if (
                    isChild &&
                    meta?.parentId &&
                    !expandedConsortia.has(meta.parentId)
                  ) {
                    return null;
                  }
                  return (
                    <TableRow
                      key={env.id}
                      hover
                      sx={{ "&:hover": { background: alpha(athenaColors.periwinkle, 0.08) } }}
                    >
                      {/* Name + kind badge */}
                      <TableCell sx={cellSx}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                            pl: isChild ? 3 : 0,
                          }}
                        >
                          {isHeader ? (
                            <IconButton
                              size="small"
                              onClick={() => toggleConsortium(String(env.id))}
                              aria-label={
                                expanded
                                  ? t(
                                      "environments.consortium_collapse",
                                      "Hide member institutions",
                                    )
                                  : t(
                                      "environments.consortium_expand",
                                      "Show member institutions",
                                    )
                              }
                              sx={{ p: 0.25, color: textSecondary, flexShrink: 0 }}
                            >
                              {expanded ? (
                                <ExpandLessIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <ExpandMoreIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          ) : (
                            isChild && <Box sx={{ width: 20, flexShrink: 0 }} />
                          )}
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              background: alpha(athenaColors.periwinkle, 0.2),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: athenaColors.periwinkle,
                              flexShrink: 0,
                            }}
                          >
                            <FolderIcon sx={{ fontSize: 16 }} />
                          </Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            sx={{ color: textPrimary, maxWidth: 280 }}
                          >
                            {env.name || t("environments.unnamed", "Unnamed")}
                          </Typography>
                          {(kind === "consortium" || kind === "institution") && (
                            <Chip
                              label={
                                kind === "consortium"
                                  ? t("environments.kind_consortium", "consortium")
                                  : t("environments.kind_institution", "institution")
                              }
                              size="small"
                              sx={{
                                height: 17,
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                bgcolor:
                                  kind === "consortium"
                                    ? alpha(athenaColors.periwinkle, 0.2)
                                    : alpha(athenaColors.lightPurple, 0.2),
                                color:
                                  kind === "consortium"
                                    ? athenaColors.periwinkle
                                    : athenaColors.lightPurple,
                              }}
                            />
                          )}
                          {isHeader && (meta?.childCount ?? 0) > 0 && (
                            <Typography
                              variant="caption"
                              sx={{ color: textSecondary, flexShrink: 0 }}
                            >
                              {t(
                                "environments.consortium_centres_count",
                                "{count} centres",
                              ).replace("{count}", String(meta?.childCount ?? 0))}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      {/* Patients */}
                      <TableCell align="right" sx={cellSx}>
                        {isSynthesized ? (
                          <span style={{ color: textSecondary }}>&mdash;</span>
                        ) : (
                          <strong>{env.patient_count ?? 0}</strong>
                        )}
                      </TableCell>
                      {/* Members */}
                      <TableCell align="right" sx={cellSx}>
                        {isSynthesized ? (
                          <span style={{ color: textSecondary }}>&mdash;</span>
                        ) : (
                          <strong>
                            {env.members_count ??
                              (env.administrators?.length || 0)}
                          </strong>
                        )}
                      </TableCell>
                      {/* Feature chips */}
                      <TableCell sx={cellSx}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {env.allow_ai_extraction && (
                            <Chip label={t("environments.chip_ai", "AI")} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                          )}
                          {env.allow_export && (
                            <Chip label={t("environments.chip_export", "Export")} size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                          )}
                          {env.require_patient_anonymization && (
                            <Chip label={t("environments.chip_anonymized", "Anon")} size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                          )}
                        </Box>
                      </TableCell>
                      {/* Billed to */}
                      <TableCell sx={{ ...cellSx, color: textSecondary }}>
                        <Typography variant="caption" noWrap sx={{ color: textSecondary, maxWidth: 160, display: "block" }}>
                          {env.owner_organization?.name || "—"}
                        </Typography>
                      </TableCell>
                      {/* Actions — suppressed for a synthesized consortium header
                          (no real environment to manage). */}
                      <TableCell align="right" sx={cellSx}>
                        {isSynthesized ? null : (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.25 }}>
                          <Tooltip title={t("environments.members", "Members")}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedEnv(env);
                                setMembersOpen(true);
                                fetchMembers(env.id);
                              }}
                              sx={{ color: textSecondary }}
                            >
                              <GroupIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("environments.backups", "Backups")}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedEnv(env);
                                setBackupOpen(true);
                              }}
                              sx={{ color: textSecondary }}
                            >
                              <BackupIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("environments.edit", "Edit")}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingEnv({ ...env });
                                setEditOpen(true);
                              }}
                              sx={{ color: textSecondary }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("environments.delete", "Delete")}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setDeletingEnv(env);
                                setDeleteExclusiveCount(null);
                                setDeleteOpen(true);
                              }}
                              sx={{ color: alpha(theme.palette.error.light, 0.85) }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
        )}
      </VideoGlassContainer>

      {/* ================================================================ */}
      {/* DIALOGS — all use Athena dark style                              */}
      {/* ================================================================ */}

      {/* ── Create Environment ─────────────────────────────────── */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={athenaDarkTitleSx}>
          {t("environments.create_new", "Create New Environment")}
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx}>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={t("environments.env_name_label", "Environment Name")}
              value={newEnvName}
              onChange={(e) => setNewEnvName(e.target.value)}
              placeholder={t("environments.env_name_placeholder", "e.g., Cancer Research 2026")}
              helperText={t(
                "environments.env_name_helper",
                "Give your environment a descriptive name.",
              )}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: "none", color: athenaColors.periwinkle }}>
            {t("action.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            sx={{ textTransform: "none", bgcolor: athenaColors.periwinkle, "&:hover": { bgcolor: alpha(athenaColors.periwinkle, 0.85) } }}
          >
            {t("action.create", "Create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Consortium (cursor_logs/074 Phase G / 068 §3) ── */}
      <Dialog
        open={createConsortiumOpen}
        onClose={() => setCreateConsortiumOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={athenaDarkTitleSx}>
          {t("consortium.create_title", "Create a consortium")}
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx}>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={t("consortium.name_label", "Consortium name")}
              value={newConsortiumName}
              onChange={(e) => setNewConsortiumName(e.target.value)}
              placeholder={t("consortium.name_placeholder", "e.g., Melanoma RWE Network")}
              helperText={t(
                "consortium.name_helper",
                "A consortium is a network you add member centres to. It is view-only across the network; each centre downloads its own patients.",
              )}
              onKeyDown={(e) => e.key === "Enter" && handleCreateConsortium()}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateConsortiumOpen(false)} sx={{ textTransform: "none", color: athenaColors.periwinkle }}>
            {t("action.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateConsortium}
            sx={{ textTransform: "none", bgcolor: athenaColors.periwinkle, "&:hover": { bgcolor: alpha(athenaColors.periwinkle, 0.85) } }}
          >
            {t("action.create", "Create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add centre to consortium ── */}
      <Dialog
        open={addCentreOpen}
        onClose={() => setAddCentreOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={athenaDarkTitleSx}>
          {t("consortium.add_centre_title", "Add a centre")}
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx}>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={t("consortium.centre_name_label", "Centre name")}
              value={newCentreName}
              onChange={(e) => setNewCentreName(e.target.value)}
              placeholder={t("consortium.centre_name_placeholder", "e.g., Hospital Clínic Barcelona")}
              helperText={t(
                "consortium.centre_name_helper",
                "Creates an institution environment in this network. It shares the consortium's billing and can download its own patients.",
              )}
              onKeyDown={(e) => e.key === "Enter" && handleAddCentre()}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddCentreOpen(false)} sx={{ textTransform: "none", color: athenaColors.periwinkle }}>
            {t("action.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleAddCentre}
            sx={{ textTransform: "none", bgcolor: athenaColors.periwinkle, "&:hover": { bgcolor: alpha(athenaColors.periwinkle, 0.85) } }}
          >
            {t("consortium.add_centre", "Add centre")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Environment ────────────────────────────────────── */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={athenaDarkTitleSx}>
          {t("environments.edit", "Edit Environment")}
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx}>
          {editingEnv && (
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label={t("environments.env_name_label", "Environment Name")}
                value={editingEnv.name || ""}
                onChange={(e) => setEditingEnv({ ...editingEnv, name: e.target.value })}
                sx={{ mb: 3 }}
              />

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: athenaColors.periwinkle,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  display: "block",
                  mb: 2,
                }}
              >
                {t("environments.feature_flags", "Feature Flags")}
              </Typography>

              <ToggleRow
                checked={editingEnv.allow_ai_extraction}
                onChange={(v) => setEditingEnv({ ...editingEnv, allow_ai_extraction: v })}
                label={t("environments.allow_ai", "Allow AI Extraction")}
                description={t(
                  "environments.allow_ai_desc",
                  "Enable AI-powered data extraction for documents in this environment.",
                )}
              />

              <ToggleRow
                checked={editingEnv.allow_export}
                onChange={(v) => setEditingEnv({ ...editingEnv, allow_export: v })}
                label={t("environments.allow_export", "Allow Data Export")}
                description={t(
                  "environments.allow_export_desc",
                  "Allow users to export patient data from this environment.",
                )}
              />

              <ToggleRow
                checked={editingEnv.require_patient_anonymization}
                onChange={(v) => setEditingEnv({ ...editingEnv, require_patient_anonymization: v })}
                label={t("environments.require_anon", "Require Anonymization")}
                description={t(
                  "environments.require_anon_desc",
                  "Hide patient names for non-admin users in this environment.",
                )}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: "none", color: athenaColors.periwinkle }}>
            {t("action.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            sx={{ textTransform: "none", bgcolor: athenaColors.periwinkle, "&:hover": { bgcolor: alpha(athenaColors.periwinkle, 0.85) } }}
          >
            {t("environments.save_changes", "Save Changes")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Environment ───────────────────────────────────── */}
      <Dialog
        open={deleteOpen}
        onClose={() => { setDeleteOpen(false); setDeleteConfirmText(""); setDeleteExclusiveCount(null); }}
        maxWidth="sm"
        fullWidth
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={{ ...athenaDarkTitleSx, color: theme.palette.error.light }}>
          <WarningAmberIcon sx={{ color: theme.palette.error.light }} />
          {t("environments.delete_title", "Delete Environment")}
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx}>
          {deletingEnv && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.8), mb: 2, lineHeight: 1.6 }}>
                {t(
                  "environments.delete_warning",
                  "This action is permanent and cannot be undone. All patients exclusive to this environment will be permanently deleted.",
                )}
              </Typography>

              <Box
                sx={{
                  my: 2,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.common.white }}>
                  {deletingEnv.name}
                </Typography>
                <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.6) }}>
                  {deletingEnv.patient_count ?? 0} patients &middot;{" "}
                  {deletingEnv.members_count ?? (deletingEnv.administrators?.length || 0)} members
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mb: 1, color: alpha(theme.palette.common.white, 0.7) }}>
                {t("environments.delete_confirm_prompt", "Type the environment name to confirm:")}
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder={deletingEnv.name || ""}
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                error={deleteConfirmText.length > 0 && deleteConfirmText !== deletingEnv.name}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setDeleteOpen(false); setDeleteConfirmText(""); setDeleteExclusiveCount(null); }}
            sx={{ textTransform: "none", color: athenaColors.periwinkle }}
          >
            {t("action.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleteConfirmText !== deletingEnv?.name || deleteLoading}
            onClick={handleDelete}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{ textTransform: "none" }}
          >
            {t("environments.delete_button", "Delete Environment")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Invite User ────────────────────────────────────────── */}
      <Dialog
        open={inviteOpen}
        onClose={() => { setInviteOpen(false); setInviteEmail(""); }}
        maxWidth="sm"
        fullWidth
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={athenaDarkTitleSx}>
          {t("environments.invite_to", "Invite to {name}").replace("{name}", selectedEnv?.name || "")}
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx}>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={t("environments.search_email_label", "Email address")}
              placeholder={t("environments.search_email_placeholder", "user@example.com")}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInvite()}
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth>
              <InputLabel sx={{ color: alpha(theme.palette.common.white, 0.6), "&.Mui-focused": { color: athenaColors.periwinkle } }}>
                {t("environments.role_label", "Role")}
              </InputLabel>
              <Select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                label={t("environments.role_label", "Role")}
              >
                {ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box>
                      <Typography variant="body2">{t(`manage_env.role_${role.value}`, role.label)}</Typography>
                      {/* Menu paper is dark (MuiMenu override), so the
                          light-surface text.secondary is buried here too. */}
                      <Typography variant="caption" color={glass.textSecondary}>
                        {t(`manage_env.role_${role.value}_desc`, role.description)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInviteOpen(false)} sx={{ textTransform: "none", color: athenaColors.periwinkle }}>
            {t("action.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={!inviteEmail.trim() || inviteLoading}
            startIcon={inviteLoading ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
            sx={{ textTransform: "none", bgcolor: athenaColors.periwinkle, "&:hover": { bgcolor: alpha(athenaColors.periwinkle, 0.85) } }}
          >
            {t("environments.send_invitation", "Send Invitation")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Members ────────────────────────────────────────────── */}
      <Dialog
        open={membersOpen}
        onClose={() => setMembersOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={athenaDarkTitleSx}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <GroupIcon sx={{ color: athenaColors.periwinkle }} />
              <Typography variant="h6" component="span" sx={{ color: theme.palette.common.white }}>
                {t("environments.members_of", "Members of {name}").replace("{name}", selectedEnv?.name || "")}
              </Typography>
            </Box>
            <Chip
              label={`${envMembers.length} ${envMembers.length !== 1 ? t("environments.members_plural", "members") : t("environments.member_singular", "member")}`}
              size="small"
              sx={{ bgcolor: alpha(athenaColors.periwinkle, 0.2), color: athenaColors.periwinkle }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ ...athenaDarkContentSx, borderColor: alpha(athenaColors.periwinkle, 0.15) }}>
          {membersLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: athenaColors.periwinkle }} />
            </Box>
          ) : (
            <>
              {/* Members table \u2014 same aesthetic as the bulk-upload review
                  table (sticky header, deepNavy surface, status chips).
                  Joan, 2026-05-28. */}
              <TableContainer
                sx={{
                  maxHeight: 440,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                  background: alpha(athenaColors.deepNavy, 0.2),
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        t("environments.col_user", "User"),
                        t("environments.col_role", "Role"),
                        t("environments.col_can_export", "Export"),
                        t("environments.col_status", "Status"),
                        "",
                      ].map((h, i) => (
                        <TableCell
                          key={i}
                          align={i === 4 ? "right" : i === 2 ? "center" : "left"}
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.common.white,
                            fontSize: "0.76rem",
                            background: alpha(athenaColors.deepNavy, 0.85),
                            backdropFilter: "blur(8px)",
                            borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {envMembers.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{
                            textAlign: "center",
                            py: 4,
                            color: alpha(theme.palette.common.white, 0.6),
                            borderBottom: "none",
                          }}
                        >
                          <GroupIcon sx={{ fontSize: 40, opacity: 0.4, display: "block", mx: "auto", mb: 1 }} />
                          {t("environments.no_members", "No members yet. Add someone below.")}
                        </TableCell>
                      </TableRow>
                    )}
                    {envMembers.map((member) => {
                      const userName =
                        `${member.user.first_name || ""} ${member.user.last_name || ""}`.trim() ||
                        member.user.username ||
                        t("manage_env.user_fallback", "User");
                      const initials = (member.user.first_name?.[0] || member.user.email[0]).toUpperCase();
                      const isPending =
                        member.status === "pending" || (!member.accepted_at && member.is_active);
                      const cellSx = {
                        color: theme.palette.common.white,
                        borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
                      };
                      return (
                        <TableRow
                          key={member.id}
                          hover
                          sx={{ "&:hover": { background: alpha(athenaColors.periwinkle, 0.08) } }}
                        >
                          <TableCell sx={cellSx}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  fontSize: "0.75rem",
                                  bgcolor: isPending
                                    ? alpha(theme.palette.warning.main, 0.2)
                                    : alpha(athenaColors.periwinkle, 0.2),
                                  color: isPending
                                    ? theme.palette.warning.main
                                    : athenaColors.periwinkle,
                                }}
                              >
                                {initials}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap sx={{ color: theme.palette.common.white }}>
                                  {userName}
                                </Typography>
                                <Typography variant="caption" noWrap sx={{ color: alpha(theme.palette.common.white, 0.5) }}>
                                  {member.user.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={member.role}
                                onChange={(e) => {
                                  if (selectedEnv) handleUpdateMemberRole(selectedEnv.id, member.id, e.target.value);
                                }}
                                size="small"
                                sx={{
                                  fontSize: "0.8rem",
                                  color: theme.palette.common.white,
                                  "& .MuiSvgIcon-root": { color: alpha(theme.palette.common.white, 0.5) },
                                  "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha(theme.palette.common.white, 0.2) },
                                }}
                              >
                                {ROLE_OPTIONS.map((r) => (
                                  <MenuItem key={r.value} value={r.value} sx={{ fontSize: "0.85rem" }}>
                                    {t(`manage_env.role_${r.value}`, r.label)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell sx={cellSx} align="center">
                            <Tooltip
                              title={
                                member.role === "viewer"
                                  ? t(
                                      "environments.export_viewer_hint",
                                      "Viewers cannot export regardless of this setting.",
                                    )
                                  : t(
                                      "environments.export_toggle_hint",
                                      "Allow this member to export and download data from this environment.",
                                    )
                              }
                              arrow
                            >
                              <span>
                                <Switch
                                  size="small"
                                  checked={
                                    member.role === "viewer"
                                      ? false
                                      : member.can_export
                                  }
                                  disabled={member.role === "viewer"}
                                  onChange={(e) => {
                                    if (selectedEnv)
                                      handleUpdateMemberExport(
                                        selectedEnv.id,
                                        member.id,
                                        e.target.checked,
                                      );
                                  }}
                                  sx={{
                                    "& .MuiSwitch-track": {
                                      backgroundColor: alpha(
                                        theme.palette.common.white,
                                        0.2,
                                      ),
                                    },
                                    "& .Mui-checked + .MuiSwitch-track": {
                                      backgroundColor: `${alpha(athenaColors.periwinkle, 0.5)} !important`,
                                    },
                                  }}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {isPending ? (
                              <Chip icon={<HourglassEmptyIcon sx={{ fontSize: 14 }} />} label={t("environments.status_pending", "Pending")} size="small" color="warning" variant="filled" sx={{ fontSize: "0.7rem", height: 22 }} />
                            ) : (
                              <Chip icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label={t("environments.status_active", "Active")} size="small" color="success" variant="filled" sx={{ fontSize: "0.7rem", height: 22 }} />
                            )}
                          </TableCell>
                          <TableCell align="right" sx={cellSx}>
                            <Tooltip title={t("environments.remove_member", "Remove member")}>
                              <IconButton
                                size="small"
                                onClick={() => { if (selectedEnv) handleRemoveMember(selectedEnv.id, member.id); }}
                                sx={{ color: alpha(theme.palette.error.light, 0.85) }}
                              >
                                <RemoveCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Inline "add user" row at the end of the table. */}
              {canInvite && selectedEnv && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px dashed ${alpha(athenaColors.periwinkle, 0.4)}`,
                    background: alpha(athenaColors.deepNavy, 0.25),
                    flexWrap: "wrap",
                  }}
                >
                  <PersonAddIcon sx={{ color: athenaColors.periwinkle, fontSize: 20 }} />
                  <TextField
                    size="small"
                    placeholder={t("environments.add_user_email", "User email")}
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleInlineAdd()}
                    sx={{
                      flex: 1,
                      minWidth: 200,
                      "& .MuiOutlinedInput-root": { color: theme.palette.common.white },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha(theme.palette.common.white, 0.2) },
                      "& input::placeholder": { color: alpha(theme.palette.common.white, 0.4) },
                    }}
                  />
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      size="small"
                      sx={{
                        fontSize: "0.8rem",
                        color: theme.palette.common.white,
                        "& .MuiSvgIcon-root": { color: alpha(theme.palette.common.white, 0.5) },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha(theme.palette.common.white, 0.2) },
                      }}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <MenuItem key={r.value} value={r.value} sx={{ fontSize: "0.85rem" }}>
                          {t(`manage_env.role_${r.value}`, r.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={handleInlineAdd}
                    disabled={!inviteEmail.trim() || inviteLoading}
                    startIcon={inviteLoading ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
                    sx={{
                      textTransform: "none",
                      bgcolor: athenaColors.periwinkle,
                      "&:hover": { bgcolor: alpha(athenaColors.periwinkle, 0.85) },
                    }}
                  >
                    {t("environments.add_user", "Add")}
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMembersOpen(false)} sx={{ textTransform: "none", color: athenaColors.periwinkle }}>
            {t("action.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Backup/Restore Dialog ──────────────────────────────── */}
      <Dialog
        open={backupOpen}
        onClose={() => { setBackupOpen(false); setRestorePreview(null); setRestoreTarget(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: athenaDarkPaperSx }}
        slotProps={{ backdrop: { sx: athenaBackdropSx } }}
      >
        <DialogTitle sx={athenaDarkTitleSx}>
          <BackupIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          {t("environments.backups_title", "Environment Backups")} — {selectedEnv?.name}
        </DialogTitle>
        <DialogContent sx={athenaDarkContentSx}>
          {/* Backup Now */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={backupLoading ? <CircularProgress size={14} /> : <BackupIcon />}
              disabled={backupLoading}
              onClick={async () => {
                if (!selectedEnv) return;
                try {
                  setBackupLoading(true);
                  await api.post(`/environments/${selectedEnv.id}/backup/`);
                  enqueueSnackbar(t("environments.backup_started", "Backup started"), { variant: "success" });
                  setTimeout(async () => {
                    const res = await api.get(`/environments/${selectedEnv.id}/backups/`);
                    setBackupRecords(res.data || []);
                  }, 3000);
                } catch (err: any) {
                  enqueueSnackbar(err.response?.data?.error || t("environments.backup_failed", "Backup failed"), { variant: "error" });
                } finally {
                  setBackupLoading(false);
                }
              }}
              sx={{ textTransform: "none" }}
            >
              {t("environments.backup_now", "Backup Now")}
            </Button>
          </Box>

          {/* Backup list */}
          {backupRecords.length === 0 ? (
            <Typography variant="body2" sx={{ color: textSecondary, textAlign: "center", py: 3 }}>
              {t("environments.no_backups", "No backups yet for this environment.")}
            </Typography>
          ) : (
            <List dense>
              {backupRecords.map((rec: any) => (
                <ListItem key={rec.id} divider>
                  <ListItemText
                    primary={new Date(rec.created_at).toLocaleString()}
                    secondary={`${rec.patient_count ?? 0} patients — ${rec.size_bytes ? `${(rec.size_bytes / 1024).toFixed(0)} KB` : "—"} — ${rec.status}`}
                    primaryTypographyProps={{ sx: { color: textPrimary, fontSize: "0.85rem" } }}
                    secondaryTypographyProps={{ sx: { color: textSecondary, fontSize: "0.75rem" } }}
                  />
                  {rec.status === "completed" && (
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={async () => {
                          if (!selectedEnv) return;
                          try {
                            setBackupLoading(true);
                            const preview = await api.post(`/environments/${selectedEnv.id}/restore/preview/`, { backup_id: rec.id });
                            setRestorePreview(preview.data);
                            setRestoreTarget(rec.id);
                          } catch (err: any) {
                            enqueueSnackbar(t("environments.preview_failed", "Preview failed"), { variant: "error" });
                          } finally {
                            setBackupLoading(false);
                          }
                        }}
                        sx={{ textTransform: "none", color: textSecondary }}
                      >
                        {t("environments.restore", "Restore")}
                      </Button>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          )}

          {/* Restore preview/confirm */}
          {restorePreview && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 1, bgcolor: alpha(athenaColors.deepNavy, 0.35) }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: textPrimary, mb: 1 }}>
                {t("environments.restore_preview", "Restore Preview")}
              </Typography>
              <Typography variant="body2" sx={{ color: textSecondary }}>
                {restorePreview.new_records} new, {restorePreview.exclusive_records} exclusive, {restorePreview.shared_records} shared
              </Typography>
              {restorePreview.shared_records > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.warning.main, mb: 1 }}>
                    {restorePreview.shared_records} record(s) are shared with other environments:
                    {restorePreview.shared_details?.slice(0, 10).map((d: any) => (
                      <Box key={d.pk} component="span" sx={{ display: "block", ml: 1, fontSize: "0.75rem" }}>
                        {d.model} #{d.pk} ({d.name}) — also in: {d.other_environments.join(", ")}
                      </Box>
                    ))}
                  </Typography>
                  <FormControl size="small" sx={{ mt: 1, minWidth: 200 }}>
                    <InputLabel>{t("environments.conflict_mode", "Conflict mode")}</InputLabel>
                    <Select
                      value={conflictMode}
                      label={t("environments.conflict_mode", "Conflict mode")}
                      onChange={(e) => setConflictMode(e.target.value as typeof conflictMode)}
                    >
                      <MenuItem value="skip_shared">{t("environments.skip_shared", "Skip shared (safest)")}</MenuItem>
                      <MenuItem value="overwrite_shared">{t("environments.overwrite_shared", "Overwrite shared")}</MenuItem>
                    </Select>
                  </FormControl>
                  {conflictMode === "overwrite_shared" && (
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: theme.palette.error.main }}>
                      {t("environments.shared_warning", "This will modify data visible to users in other environments.")}
                    </Typography>
                  )}
                </Box>
              )}
              <Box sx={{ mt: 2, display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  onClick={() => { setRestorePreview(null); setRestoreTarget(null); }}
                  sx={{ textTransform: "none", color: textSecondary }}
                >
                  {t("action.cancel", "Cancel")}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  disabled={backupLoading}
                  onClick={async () => {
                    if (!selectedEnv || !restoreTarget) return;
                    try {
                      setBackupLoading(true);
                      await api.post(`/environments/${selectedEnv.id}/restore/`, {
                        backup_id: restoreTarget,
                        conflict_mode: conflictMode,
                      });
                      enqueueSnackbar(t("environments.restore_started", "Restore started"), { variant: "success" });
                      setRestorePreview(null);
                      setRestoreTarget(null);
                    } catch (err: any) {
                      enqueueSnackbar("Restore failed", { variant: "error" });
                    } finally {
                      setBackupLoading(false);
                    }
                  }}
                  sx={{ textTransform: "none" }}
                >
                  {t("environments.confirm_restore", "Confirm Restore")}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setBackupOpen(false); setRestorePreview(null); setRestoreTarget(null); }}
            sx={{ textTransform: "none", color: athenaColors.periwinkle }}
          >
            {t("action.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Tier gate banner ──────────────────────────────────── */}
      {!canInvite && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            background: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            backdropFilter: "blur(8px)",
          }}
        >
          <Typography variant="body2" sx={{ color: textPrimary }}>
            {t(
              "tier_gate.invite_upgrade_prompt",
              "Upgrade to Professional or higher to invite team members and collaborate on patient data within your environments.",
            )}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ManageEnvironments;
